import React, { useEffect, useState } from "react";
import {
    Box,
    Paper,
    Grid,
    Typography,
    Divider,
    useTheme,
    Alert,
    Stack,
    Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";

import FormInput from "../inputs/FormInput";
import LanguageToggle from "../global/LanguageToggle";
import PermissionsTree from "./PermissionsTree";

import { useCreateRole } from "../../hooks/roles/useCreateRole";
import { useUpdateRole } from "../../hooks/roles/useUpdateRole";
import { useGetPermissions } from "../../hooks/roles/useGetPermissions";

const BUILT_IN_ROLES = new Set(["admin", "super_admin", "customer"]);
const NAME_REGEX = /^[a-z][a-z0-9_]{2,40}$/;

const FormAddEditRole = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || "en").startsWith("ar");
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";
    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");
    const theme = useTheme();

    const isBuiltIn = isEdit && BUILT_IN_ROLES.has(currentData?.name);

    const [name, setName] = useState("");
    const [labelAr, setLabelAr] = useState("");
    const [labelEn, setLabelEn] = useState("");
    const [permissionIds, setPermissionIds] = useState([]);

    const { grouped, isLoading: isLoadingPerms } = useGetPermissions();
    const { createRoleMutate, isCreating } = useCreateRole();
    const { updateRoleMutate, isUpdating } = useUpdateRole();
    const loadingSubmit = isCreating || isUpdating;

    useEffect(() => {
        if (isEdit && currentData) {
            setName(currentData.name || "");
            setLabelAr(currentData.label?.ar || "");
            setLabelEn(currentData.label?.en || "");
            setPermissionIds(
                Array.isArray(currentData.permissions)
                    ? currentData.permissions.map((p) => (typeof p === "object" ? p.id : p))
                    : Array.isArray(currentData.permission_ids)
                    ? currentData.permission_ids
                    : []
            );
        } else {
            setName("");
            setLabelAr("");
            setLabelEn("");
            setPermissionIds([]);
        }
    }, [isEdit, currentData]);

    const nameInvalid = !isEdit && name && !NAME_REGEX.test(name);

    const submit = (e) => {
        e.preventDefault();
        const payload = {
            ...(isEdit ? {} : { name }),
            label: {
                en: labelEn || labelAr,
                ar: labelAr || labelEn,
            },
            permission_ids: permissionIds,
        };

        const onDone = { onSuccess: () => onSubmitSuccess && onSubmitSuccess() };

        if (isEdit && currentData?.id) {
            updateRoleMutate({ id: currentData.id, data: payload }, onDone);
        } else {
            createRoleMutate(payload, onDone);
        }
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 4 },
                mt: 2,
                direction: isArabic ? "rtl" : "ltr",
                borderRadius: 4,
                border: `1px solid ${theme.palette.divider}`,
            }}
        >
            <Box component="form" onSubmit={submit} sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ sm: "center" }}
                    spacing={2}
                >
                    <Box>
                        <Typography variant="h5" fontWeight="bold" color="primary">
                            {isEdit
                                ? (isArabic ? "تعديل الدور" : "Edit Role")
                                : (isArabic ? "إضافة دور جديد" : "Add New Role")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {isArabic
                                ? "حدّد الاسم التقني، العنوان المترجم، والصلاحيات الممنوحة."
                                : "Set the technical name, translatable label, and granted permissions."}
                        </Typography>
                    </Box>
                    <LanguageToggle activeLang={activeLang} onChange={setActiveLang} showLabel />
                </Stack>

                {isBuiltIn && (
                    <Alert severity="info">
                        {isArabic
                            ? `الدور "${currentData?.name}" مدمج — لا يمكن تغيير الاسم التقني، فقط العنوان والصلاحيات.`
                            : `"${currentData?.name}" is a built-in role — only the label and permissions can be changed.`}
                    </Alert>
                )}

                <Grid container spacing={3}>
                    {/* Technical name */}
                    <Grid item xs={12} md={4}>
                        <FormInput
                            label={isArabic ? "الاسم التقني" : "Technical Name"}
                            placeholder="e.g: store_manager"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value.toLowerCase().replace(/\s+/g, "_"))}
                            disabled={isEdit}
                            required={!isEdit}
                            error={!!nameInvalid}
                            helperText={
                                isEdit
                                    ? (isArabic ? "لا يمكن تعديل الاسم التقني" : "Cannot change technical name")
                                    : nameInvalid
                                        ? (isArabic
                                            ? "أحرف صغيرة، أرقام و _ فقط (3-40 حرفاً، يبدأ بحرف)"
                                            : "lowercase letters/digits/_ only (3-40 chars, starts with a letter)")
                                        : (isArabic ? "مثال: store_manager" : "e.g: store_manager")
                            }
                        />
                    </Grid>

                    {/* Bilingual label */}
                    {activeLang === "ar" && (
                        <Grid item xs={12} md={4}>
                            <FormInput
                                label="العنوان (عربي)"
                                placeholder="مثال: مدير المتجر"
                                type="text"
                                value={labelAr}
                                onChange={(e) => setLabelAr(e.target.value)}
                                required={false}
                            />
                        </Grid>
                    )}
                    {activeLang === "en" && (
                        <Grid item xs={12} md={4}>
                            <FormInput
                                label="Label (English)"
                                placeholder="e.g: Store Manager"
                                type="text"
                                value={labelEn}
                                onChange={(e) => setLabelEn(e.target.value)}
                                required={false}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Divider sx={{ mb: 2 }} />
                        {isLoadingPerms ? (
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                                {isArabic ? "جاري تحميل الصلاحيات..." : "Loading permissions..."}
                            </Typography>
                        ) : (
                            <PermissionsTree
                                grouped={grouped}
                                selectedIds={permissionIds}
                                onChange={setPermissionIds}
                                disabled={loadingSubmit}
                            />
                        )}
                    </Grid>
                </Grid>

                <Divider />

                <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                        variant="outlined"
                        onClick={() => onSubmitSuccess && onSubmitSuccess()}
                        disabled={loadingSubmit}
                        sx={{ borderRadius: 2, px: 4 }}
                    >
                        {isArabic ? "إلغاء" : "Cancel"}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loadingSubmit || (!isEdit && (!name || nameInvalid))}
                        sx={{
                            minWidth: 160,
                            borderRadius: 2,
                            fontWeight: "bold",
                            boxShadow: theme.shadows[4],
                        }}
                    >
                        {loadingSubmit
                            ? (isArabic ? "جاري الحفظ..." : "Saving...")
                            : (isEdit ? (isArabic ? "تحديث" : "Update") : (isArabic ? "إنشاء" : "Create"))}
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
};

export default FormAddEditRole;
